'use client'

import cx from 'classnames'
import { useSwipe } from 'components/_hooks/useSwipe'
import { clamp } from 'components/_utils/mathUtils'
import { Img } from 'components/basic/Img'
import { Button } from 'components/Phantom/Button'
import { Type } from 'components/Type'
import { amChangeSlideImageCarousel, amSwipeImageCarousel } from 'events/amplitude'
import useIsMobile from 'hooks/useIsMobile'
import { FC, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import styles from './UserReviews.module.scss'
import { ReviewItemProps, ReviewsSliderHandle, ReviewsSliderProps, UserReviewsProps } from './UserReviews.types'

const ITEMS: ReviewItemProps[] = [
	{
		text: '“Really enjoy taking Sleep Elixir. It helps me relax.”',
		author: 'Jess L.',
		subtitle: 'Executive',
		img: 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/sleep-elixir/7218hr08fh0h103h10321%C4%91.png',
	},
	{
		text: '“My deep and REM sleep have both improved while taking Sleep Elixir.”',
		author: 'Bryan R.',
		subtitle: 'Tech founder',
		img: 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/sleep-elixir/bray-r-sleep-elixir.png',
	},
	{
		text: '“Knowing that I’m taking natural supplements is wonderful.”',
		author: 'Tori B.',
		subtitle: 'Iron man runner',
		img: 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/sleep-elixir/ff4d2e98c6bc9107c13cccb86f813b69.png',
	},
	{
		text: '“I definitely noticed an improvement in falling asleep.”',
		author: 'Mitch D.',
		subtitle: 'Dad and business owner',
		img: 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/sleep-elixir/mitch-d-sleep-elixir.png',
	},
]

const GeneralItems: ReviewItemProps[] = [
	{
		text: '“I’m no longer worried about getting enough sleep. Being well rested allows me to focus on taking care of my health in other ways.”',
		author: 'Jennifer H.',
		img: 'https://res.cloudinary.com/eightsleep/image/upload/ugc-general-reviews-1_bdg9it.jpg',
	},
	{
		text: '“Before the Pod, I was waking up every single night or even sleeping on the floor to cool down.” ',
		author: 'Samuel G.',
		img: 'https://res.cloudinary.com/eightsleep/image/upload/ugc-general-reviews-2_zvpzui.jpg',
	},
	{
		text: '“From optimizing my workout schedule based on heart rate stats and adjusting temperature settings for peaceful sleep with my husband, the Pod has helped dramatically.”',
		author: 'Shaye E.',
		img: 'https://res.cloudinary.com/eightsleep/image/upload/ugc-general-reviews-3_ofjs37.jpg',
	},
	{
		text: '“My partner loves me… but not my snoring. The Pod has been the answer to peaceful sleep and energized mornings.”',
		author: 'Luke L.',
		img: 'https://res.cloudinary.com/eightsleep/image/upload/ugc-general-reviews-4_wsmffv.jpg',
	},
]

export const GeneralUserReviews: FC<Partial<UserReviewsProps>> = (props) => {
	return (
		<UserReviews
			showStars
			header={['Thousands of reviews', 'from extraordinary people like you']}
			cta={{
				href: '/wall-of-love',
				text: 'See more reviews',
				onClick: () => {
					localStorage.setItem('wallOfLoveFilter', 'Everyday love')
				},
			}}
			items={GeneralItems}
			{...props}
		/>
	)
}

export const UserReviews: FC<UserReviewsProps> = (props) => {
	const { header = ['Tailored for busy minds seeking deep, restorative rest'], cta, showStars, items = ITEMS } = props
	const isMobile = useIsMobile(1023)

	return (
		<section className={styles.container}>
			<header className={styles.header}>
				<div className={cx(styles.header_content, cta && styles.header_content_with_cta)}>
					<Type.Headline2
						className={styles.heading}
						animateOnScroll
					>
						{header.map((h) => (
							<span key={h}>{h}</span>
						))}
					</Type.Headline2>

					{cta && (
						<Button.Dark
							id="user-reviews-cta"
							href={cta.href}
							className={styles.cta}
							onClick={cta.onClick}
						>
							{cta.text}
						</Button.Dark>
					)}
				</div>

				{showStars && <Stars />}
			</header>

			{isMobile ? (
				<ReviewsMobile
					startIndex={0}
					items={items}
				/>
			) : (
				<ReviewsDesktop items={items} />
			)}
		</section>
	)
}

const Stars = () => {
	return (
		<div className={styles.stars}>
			<svg
				width="161"
				height="27"
				viewBox="0 0 161 27"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<path
					d="M12.928 1.72662C13.3864 0.638002 14.9474 0.638003 15.4057 1.72662L18.1107 8.15182C18.3041 8.61075 18.7409 8.92432 19.2424 8.96404L26.2638 9.52015C27.4534 9.61437 27.9358 11.081 27.0294 11.8481L21.6799 16.3751C21.2978 16.6985 21.1309 17.2059 21.2476 17.6893L22.882 24.4582C23.1589 25.605 21.8961 26.5116 20.8775 25.897L14.8662 22.2696C14.4369 22.0105 13.8969 22.0105 13.4675 22.2696L7.45616 25.897C6.43766 26.5116 5.17478 25.605 5.45168 24.4582L7.08607 17.6893C7.20282 17.2059 7.03594 16.6985 6.65385 16.3751L1.30425 11.8481C0.397871 11.081 0.880253 9.61437 2.06989 9.52015L9.09132 8.96404C9.59285 8.92432 10.0297 8.61075 10.2229 8.15182L12.928 1.72662Z"
					fill="black"
				/>
				<path
					d="M46.2615 1.72662C46.7199 0.638002 48.2809 0.638003 48.7392 1.72662L51.4442 8.15182C51.6376 8.61075 52.0744 8.92432 52.5759 8.96404L59.5973 9.52015C60.7869 9.61437 61.2693 11.081 60.3629 11.8481L55.0134 16.3751C54.6313 16.6985 54.4644 17.2059 54.5811 17.6893L56.2155 24.4582C56.4924 25.605 55.2296 26.5116 54.211 25.897L48.1997 22.2696C47.7704 22.0105 47.2304 22.0105 46.801 22.2696L40.7897 25.897C39.7712 26.5116 38.5083 25.605 38.7852 24.4582L40.4196 17.6893C40.5363 17.2059 40.3694 16.6985 39.9873 16.3751L34.6377 11.8481C33.7314 11.081 34.2137 9.61437 35.4034 9.52015L42.4248 8.96404C42.9263 8.92432 43.3632 8.61075 43.5564 8.15182L46.2615 1.72662Z"
					fill="black"
				/>
				<path
					d="M79.5945 1.72662C80.0529 0.638002 81.6139 0.638003 82.0722 1.72662L84.7772 8.15182C84.9706 8.61075 85.4074 8.92432 85.9089 8.96404L92.9303 9.52015C94.1199 9.61437 94.6023 11.081 93.6959 11.8481L88.3464 16.3751C87.9643 16.6985 87.7974 17.2059 87.9141 17.6893L89.5485 24.4582C89.8254 25.605 88.5626 26.5116 87.544 25.897L81.5327 22.2696C81.1034 22.0105 80.5634 22.0105 80.134 22.2696L74.1227 25.897C73.1042 26.5116 71.8413 25.605 72.1182 24.4582L73.7526 17.6893C73.8693 17.2059 73.7024 16.6985 73.3204 16.3751L67.9708 11.8481C67.0644 11.081 67.5468 9.61437 68.7364 9.52015L75.7578 8.96404C76.2594 8.92432 76.6962 8.61075 76.8894 8.15182L79.5945 1.72662Z"
					fill="black"
				/>
				<path
					d="M112.928 1.72662C113.386 0.638002 114.947 0.638003 115.406 1.72662L118.111 8.15182C118.304 8.61075 118.741 8.92432 119.242 8.96404L126.264 9.52015C127.453 9.61437 127.936 11.081 127.029 11.848L121.68 16.3751C121.298 16.6985 121.131 17.2059 121.248 17.6893L122.882 24.4582C123.159 25.605 121.896 26.5116 120.878 25.897L114.866 22.2696C114.437 22.0105 113.897 22.0105 113.467 22.2696L107.456 25.897C106.438 26.5116 105.175 25.605 105.452 24.4582L107.086 17.6893C107.203 17.2059 107.036 16.6985 106.654 16.3751L101.304 11.848C100.398 11.081 100.88 9.61437 102.07 9.52015L109.091 8.96404C109.593 8.92432 110.03 8.61075 110.223 8.15182L112.928 1.72662Z"
					fill="black"
				/>
				<path
					fillRule="evenodd"
					clipRule="evenodd"
					d="M160.691 10.2628L160.797 10.5961C160.885 10.8477 160.811 11.1273 160.611 11.3028L154.149 16.8361L156.064 25.1294C156.128 25.3945 156.029 25.6728 155.811 25.8361L155.518 26.0361C155.405 26.1254 155.264 26.1725 155.12 26.1694C154.998 26.172 154.878 26.1396 154.774 26.0761L147.501 21.6361L140.267 26.0761C140.163 26.1396 140.043 26.172 139.921 26.1694C139.777 26.1725 139.636 26.1254 139.523 26.0361L139.19 25.8361C138.972 25.6728 138.873 25.3945 138.937 25.1294L140.852 16.8361L134.403 11.3161C134.193 11.1417 134.113 10.8543 134.204 10.5961L134.35 10.2628C134.428 9.99931 134.662 9.81282 134.935 9.79609L143.432 9.11609L146.689 1.26275C146.794 1.00195 147.048 0.832396 147.328 0.836086H147.673C147.951 0.830176 148.202 1.00148 148.298 1.26275L151.609 9.11609L160.106 9.79609C160.379 9.81282 160.613 9.99931 160.691 10.2628ZM152.979 22.2494L151.49 16.0094L156.37 11.8361L149.974 11.3294L147.501 5.38275V18.9028L152.979 22.2494Z"
					fill="black"
				/>
			</svg>
			<Type.Headchapter>(4.6 stars) • 1,309 reviews</Type.Headchapter>
		</div>
	)
}

export const ReviewsDesktop = ({ items }: { items: ReviewItemProps[] }) => {
	return (
		<div className={styles.grid}>
			{items.map((item, index) => (
				<ReviewItem
					key={index}
					item={item}
				/>
			))}
		</div>
	)
}

export const ReviewsMobile = forwardRef<ReviewsSliderHandle, ReviewsSliderProps>((props, ref) => {
	const { startIndex, items = ITEMS } = props

	const sliderRef = useRef<HTMLUListElement>(null)

	const [currentIndex, setCurrentIndex] = useState(startIndex)

	useSwipe(
		sliderRef,
		(direction: 'left' | 'right') => {
			const length = items.length
			amSwipeImageCarousel(direction, props.id)
			if (direction === 'left') {
				setCurrentIndex((prev) => Math.min(length - 1, prev + 1))
			} else if (direction === 'right') {
				setCurrentIndex((prev) => Math.max(0, prev - 1))
			}
		},
		50,
		500,
		[]
	)

	useImperativeHandle(ref, () => ({
		goToSlide: (index: number) => {
			setCurrentIndex(index)
			amChangeSlideImageCarousel(props.id, index)
		},
	}))

	useEffect(() => {
		setCurrentIndex(startIndex)
		amChangeSlideImageCarousel(props.id, startIndex)
	}, [startIndex, props.id])

	const clampedIndex = clamp(currentIndex, 0, items.length - 1)

	return (
		<div className={styles.slider_wrapper}>
			<ul
				className={styles.media_stack}
				ref={sliderRef}
			>
				{items.map((item, index) => (
					<li
						key={`${index}`}
						className={styles.media_item}
						style={{
							transform: `translateX(calc(${(index - clampedIndex) * 100}%))`,
							opacity: index === clampedIndex ? 1 : 0.3,
						}}
					>
						<Button.Empty
							id={`review-item-${index}`}
							onClick={() => setCurrentIndex(index)}
						>
							<ReviewItem item={item} />
						</Button.Empty>
					</li>
				))}
			</ul>
			<div className={styles.dots_container}>
				{items.map((_, index) => (
					<div
						key={index}
						className={styles.dot}
						data-active={index === clampedIndex}
					></div>
				))}
			</div>
		</div>
	)
})

export const ReviewItem = ({ item }: { item: ReviewItemProps }) => {
	const { text, author, subtitle } = item

	return (
		<div className={styles.review_item}>
			<Img
				src={item.img}
				alt={item.author}
				className={styles.review_item_img}
				simple
			/>
			<div className={styles.review_item_text}>
				<Type.Body2>{text}</Type.Body2>
				{/* <Type.Headline5 className={styles.review_item_author}>{author}</Type.Headline5> */}
				<Type.Body2 className={styles.review_item_subtitle}>{author}</Type.Body2>
			</div>
		</div>
	)
}
